import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';

export default function Contact() {
  return (
    <div className="bg-white">
      {/* Cinematic Header */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden">
        <img 
          src="/images/about3.jpg" 
          alt="Contact Us" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gh-blue/85 backdrop-blur-sm"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl md:text-7xl font-black text-white italic uppercase tracking-tighter">
            Get In <span className="text-gh-orange">Touch</span>
          </h1>
          <div className="w-24 h-2 bg-gh-orange mx-auto mt-4"></div>
          <p className="text-blue-100 mt-6 max-w-xl mx-auto font-medium">
            Have questions about sponsorship, volunteering, or visiting our sanctuary in Mityana? 
            We are here to answer.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto py-24 px-6 grid lg:grid-cols-2 gap-20">
        
        {/* Left Side: Contact Information */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-gh-blue text-3xl font-black uppercase italic mb-8 flex items-center gap-3">
            <MessageSquare className="text-gh-orange" /> Contact Details
          </h2>
          
          <div className="space-y-10">
            <div className="flex items-start gap-6 group">
              <div className="bg-slate-50 p-4 rounded-2xl text-gh-orange shadow-sm group-hover:bg-gh-orange group-hover:text-white transition-all duration-300">
                <MapPin size={28} />
              </div>
              <div>
                <h4 className="font-black text-gh-blue uppercase tracking-widest text-sm mb-1">Our Location</h4>
                <p className="text-gray-500 font-medium leading-relaxed">
                  Giving Hands Ministry Mityana<br />
                  Mityana District, Central Uganda
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6 group">
              <div className="bg-slate-50 p-4 rounded-2xl text-gh-blue shadow-sm group-hover:bg-gh-blue group-hover:text-white transition-all duration-300">
                <Phone size={28} />
              </div>
              <div>
                <h4 className="font-black text-gh-blue uppercase tracking-widest text-sm mb-1">Phone Number</h4>
                <p className="text-gray-500 font-medium">+256 702 601 805</p>
                <p className="text-xs text-gh-orange font-bold mt-1 uppercase italic">Available on WhatsApp</p>
              </div>
            </div>

            <div className="flex items-start gap-6 group">
              <div className="bg-slate-50 p-4 rounded-2xl text-gh-orange shadow-sm group-hover:bg-gh-orange group-hover:text-white transition-all duration-300">
                <Mail size={28} />
              </div>
              <div>
                <h4 className="font-black text-gh-blue uppercase tracking-widest text-sm mb-1">Email Address</h4>
                <p className="text-gray-500 font-medium">givinghandsministries27@gmail.com</p>
              </div>
            </div>
          </div>

          {/* Social Proof / Trust Note */}
          <div className="mt-16 p-8 bg-gh-blue rounded-[2rem] text-white">
            <h4 className="font-black uppercase italic mb-2">Visit Us</h4>
            <p className="text-blue-100 text-sm leading-relaxed">
              We operate with full transparency. If you are in Uganda, we welcome you to visit 
              our ministry and see the impact of your support firsthand. Please call in advance 
              to schedule a visit.
            </p>
          </div>
        </motion.div>

        {/* Right Side: Contact Form */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white p-10 rounded-[3rem] shadow-2xl border border-slate-100 relative"
        >
          <div className="absolute -top-6 -right-6 bg-gh-orange text-white p-4 rounded-2xl rotate-12 shadow-lg hidden md:block">
            <Send size={24} />
          </div>

          <form 
            action="https://formspree.io/f/xdalzaqo" 
            method="POST" 
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gh-blue mb-2 ml-1">Full Name</label>
                <input 
                  type="text" 
                  name="name"
                  className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-gh-orange/30 focus:bg-white outline-none transition-all font-medium"
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gh-blue mb-2 ml-1">Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-gh-orange/30 focus:bg-white outline-none transition-all font-medium"
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gh-blue mb-2 ml-1">Subject</label>
              <input 
                type="text" 
                name="subject"
                className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-gh-orange/30 focus:bg-white outline-none transition-all font-medium"
                placeholder="How can we help?"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gh-blue mb-2 ml-1">Your Message</label>
              <textarea 
                name="message"
                rows="5"
                className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-gh-orange/30 focus:bg-white outline-none transition-all font-medium"
                placeholder="Tell us more about your heart for the children..."
                required
              ></textarea>
            </div>

            <button 
              type="submit"
              className="w-full bg-gh-blue text-white font-black py-5 rounded-2xl flex items-center justify-center gap-3 hover:bg-blue-900 transition-all shadow-xl active:scale-95 uppercase tracking-widest"
            >
              Send Message <Send size={20} />
            </button>
          </form>
        </motion.div>
      </section>
    </div>
  );
}